import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, Briefcase, Users, Bookmark, Clock, Plus,
  Sparkles, ArrowRight, CornerDownLeft, ArrowUp, ArrowDown, X,
} from 'lucide-react'
import { AnimatedEmpty } from '@/components/empty/AnimatedEmpty'
import { LoadError } from '@/components/empty/LoadError'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/contexts/AuthContext'
import { useGlobalSearch } from '@/hooks/useGlobalSearch'
import { useRecentSearches } from './useRecentSearches'
import { SearchResultRowV2, GlyphKind } from './SearchResultRowV2'
import { SegmentLayer, useSegmentLayer } from '@/components/ui/segmented'
import { chipLabel, type DateRangeFilter } from '@/lib/candidateDates'

type Scope = 'all' | 'candidates' | 'jobs' | 'saved'

interface SavedViewLite { id: string; name: string }

interface FlatRow {
  key: string
  glyph: GlyphKind
  initials?: string
  icon?: React.ComponentType<{ className?: string }>
  title: string
  sub?: React.ReactNode
  rightMeta?: React.ReactNode
  onSelect: () => void
}

interface GlobalSearchPanelProps {
  query: string
  onQueryChange: (q: string) => void
  onClose: () => void
  onOpenCandidate: (id: string) => void
  /** Reports when a search is in flight, so the input can show its inline spinner. */
  onLoadingChange?: (loading: boolean) => void
}

const SCOPES: { id: Scope; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'all',        label: 'All',        icon: Search },
  { id: 'candidates', label: 'Candidates', icon: Users },
  { id: 'jobs',       label: 'Jobs',       icon: Briefcase },
  { id: 'saved',      label: 'Saved',      icon: Bookmark },
]

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('')
}

export function GlobalSearchPanel({
  query, onQueryChange, onClose, onOpenCandidate, onLoadingChange,
}: GlobalSearchPanelProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [scope, setScope] = useState<Scope>('all')
  const [askMode, setAskMode] = useState(false)
  const segRef = useSegmentLayer<HTMLDivElement>()
  const [highlighted, setHighlighted] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const trimmed = query.trim()
  const hasQuery = trimmed.length >= 2

  const { results, isLoading, totalCounts, error: searchError, retry: retrySearch, resultsFor } = useGlobalSearch(hasQuery && !askMode ? query : '', { limit: 5 })
  const recent = useRecentSearches(user?.id ?? null)
  const searching = isLoading && !askMode
  useEffect(() => {
    onLoadingChange?.(searching)
  }, [searching, onLoadingChange])
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange])

  // Saved views (candidates context — most used)
  const { data: savedViews = [] } = useQuery({
    queryKey: ['global-search-saved-views', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<SavedViewLite[]> => {
      if (!user) return []
      const { data } = await (supabase as any)
        .from('saved_views')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('page_context', 'candidates')
        .order('updated_at', { ascending: false })
        .limit(20)
      return (data ?? []) as SavedViewLite[]
    },
  })

  // Ask Gio results
  const [askResult, setAskResult] = useState<null | {
    skills: string[]; cities: string[]; countries: string[]; seniorityLevels: string[]; stages: string[]; statuses: string[]; query: string
    dates?: DateRangeFilter[]
    jobTitle?: string
  }>(null)
  const [askLoading, setAskLoading] = useState(false)
  const [askError, setAskError] = useState<string | null>(null)

  useEffect(() => {
    if (!askMode || !hasQuery) { setAskResult(null); return }
    let cancelled = false
    setAskLoading(true)
    setAskError(null)
    const t = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('candidates-nl-search', {
          body: { prompt: trimmed, options: {} },
        })
        if (cancelled) return
        if (error) throw error
        setAskResult(data)
      } catch {
        if (!cancelled) setAskError('Couldn’t reach Gio. Try again, or open it in Candidates.')
      } finally {
        if (!cancelled) setAskLoading(false)
      }
    }, 700)
    return () => { cancelled = true; clearTimeout(t) }
  }, [askMode, trimmed, hasQuery])




  const filteredSaved = useMemo(() => {
    if (!hasQuery) return savedViews.slice(0, 5)
    const q = trimmed.toLowerCase()
    return savedViews.filter(v => v.name.toLowerCase().includes(q)).slice(0, 5)
  }, [savedViews, hasQuery, trimmed])

  // Build the flat row list for keyboard nav
  const rows: FlatRow[] = useMemo(() => {
    const r: FlatRow[] = []
    const commit = (q: string, s: Scope) => recent.push(q, s)

    // Ask Gio mode
    if (askMode) {
      if (askResult) {
        r.push({
          key: 'ask-open',
          glyph: 'ai',
          icon: Sparkles,
          title: `Open in Candidates`,
          sub: 'Apply Gio\u2019s interpretation',
          rightMeta: <CornerDownLeft className="h-3 w-3" />,
          // The Candidates page runs the request with its own filter options.
          onSelect: () => { commit(trimmed, 'candidates'); navigate(`/candidates?ask=${encodeURIComponent(trimmed)}`); onClose() },
        })
      }
      return r
    }

    // Empty state: recents + jump-to + commands
    if (!hasQuery) {
      for (const it of recent.items) {
        r.push({
          key: `recent-${it.ts}`,
          glyph: 'recent',
          icon: Clock,
          title: it.query,
          sub: `in ${it.scope}`,
          rightMeta: <span>{new Date(it.ts).toLocaleDateString()}</span>,
          onSelect: () => { onQueryChange(it.query); setScope(it.scope as Scope) },
        })
      }
      for (const v of savedViews.slice(0, 4)) {
        r.push({
          key: `saved-${v.id}`,
          glyph: 'saved',
          icon: Bookmark,
          title: v.name,
          sub: 'Saved search',
          rightMeta: <span>Searches</span>,
          onSelect: () => { navigate(`/candidates?view=${v.id}`); onClose() },
        })
      }
      // Commands
      r.push({
        key: 'cmd-add-candidate', glyph: 'command', icon: Plus,
        title: 'Add candidate',
        rightMeta: <span className="font-mono">⌘N</span>,
        onSelect: () => { navigate('/candidates?new=1'); onClose() },
      })
      r.push({
        key: 'cmd-create-job', glyph: 'command', icon: Plus,
        title: 'Create job',
        rightMeta: <span className="font-mono">⌘J</span>,
        onSelect: () => { navigate('/jobs?new=1'); onClose() },
      })
      return r
    }

    // Results mode
    if (scope === 'all' || scope === 'candidates') {
      const cands = results.filter(x => x.type === 'candidate')
      for (const c of cands) {
        r.push({
          key: `cand-${c.id}`,
          glyph: 'avatar',
          initials: initialsOf(c.title),
          title: c.title,
          sub: c.subtitle,
          rightMeta: c.metadata ? <span className="truncate max-w-[120px]">{c.metadata}</span> : undefined,
          onSelect: () => { commit(trimmed, 'candidates'); onOpenCandidate(c.id); onClose() },
        })
      }
    }
    if (scope === 'all' || scope === 'jobs') {
      const jobs = results.filter(x => x.type === 'job')
      for (const j of jobs) {
        r.push({
          key: `job-${j.id}`,
          glyph: 'job',
          icon: Briefcase,
          title: j.title,
          sub: j.subtitle,
          rightMeta: <span>Jobs</span>,
          onSelect: () => { commit(trimmed, 'jobs'); navigate(j.route); onClose() },
        })
      }
    }
    if (scope === 'all' || scope === 'saved') {
      for (const v of filteredSaved) {
        r.push({
          key: `sv-${v.id}`,
          glyph: 'saved',
          icon: Bookmark,
          title: v.name,
          sub: 'Saved search',
          rightMeta: <span>Searches</span>,
          onSelect: () => { commit(trimmed, 'saved'); navigate(`/candidates?view=${v.id}`); onClose() },
        })
      }
    }

    // No results recovery
    if (r.length === 0 && hasQuery && !isLoading && !searchError) {
      r.push({
        key: 'no-ask',
        glyph: 'ai',
        icon: Sparkles,
        title: `Ask Gio: \u201Cfind ${trimmed}\u201D`,
        onSelect: () => { setAskMode(true) },
      })
      r.push({
        key: 'no-add',
        glyph: 'command',
        icon: Plus,
        title: `Add \u201C${trimmed}\u201D as a new candidate`,
        onSelect: () => { navigate('/candidates?new=1'); onClose() },
      })
    }
    return r
  }, [askMode, askResult, hasQuery, recent, savedViews, results, scope, filteredSaved, trimmed, isLoading, searchError, navigate, onClose, onOpenCandidate, onQueryChange])

  useEffect(() => { setHighlighted(0) }, [rows.length, scope, askMode])

  // Keyboard handling
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(p => Math.min(rows.length - 1, p + 1)); return }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(p => Math.max(0, p - 1)); return }
    if (e.key === 'Tab') {
      e.preventDefault()
      const idx = SCOPES.findIndex(s => s.id === scope)
      const next = SCOPES[(idx + (e.shiftKey ? -1 : 1) + SCOPES.length) % SCOPES.length]
      setScope(next.id)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const row = rows[highlighted]
      if (row) row.onSelect()
      return
    }
  }, [rows, highlighted, scope, onClose])

  // Only once the results belong to what's typed: never during the 150ms debounce.
  const settled = resultsFor === trimmed
  const noMatches = hasQuery && !askMode && !isLoading && settled && !searchError && rows.length > 0 && rows.every(r => r.key.startsWith('no-'))

  // Top "See all N results" banner when mixed and has query
  const totalAll = totalCounts.jobs + totalCounts.candidates
  const showSeeAll = hasQuery && !askMode && scope === 'all' && totalAll > 0

  return (
    <div
      role="listbox"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
      // §12: the results box keeps one height while you type, whatever the result count.
      className="w-[600px] h-[min(560px,calc(100dvh-96px))] flex flex-col rounded-[12px] border border-border bg-popover text-popover-foreground shadow-[0_12px_32px_-8px_rgba(0,0,0,0.18)] overflow-hidden"
    >
      {/* Scope chip bar */}
      <div className="flex items-center gap-1 px-3 pt-3 pb-2 border-b border-border">
        {/* §14: the dark scope fill is one sliding SegmentLayer; Ask Gio sits outside it. */}
        <div ref={segRef} className="gio-seg inline-flex items-center gap-1">
          {SCOPES.map(s => {
            const Icon = s.icon
            const active = !askMode && scope === s.id
            return (
              <button
                key={s.id}
                type="button"
                data-seg-option
                data-active={active || undefined}
                onClick={() => { setAskMode(false); setScope(s.id) }}
                className={cn(
                  'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md font-poppins font-medium text-[12px] tracking-[-0.005em] transition-colors',
                  active
                    ? 'text-[#FFFCF9]'
                    : 'text-virgilio-muted hover:text-foreground hover:bg-[#F1F0EC]'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {s.label}
              </button>
            )
          })}
          <SegmentLayer fill="#0d0d09" />
        </div>
        <button
          type="button"
          onClick={() => setAskMode(v => !v)}
          className={cn(
            'ml-auto inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md font-poppins font-medium text-[12px] tracking-[-0.005em] transition-colors',
            askMode
              ? 'bg-violet-100 text-violet-700'
              : 'text-violet-600 hover:bg-violet-50'
          )}
        >
          <Sparkles className="h-3.5 w-3.5" />
          Ask Gio
        </button>
      </div>

      {/* See-all banner */}
      {showSeeAll && (
        <button
          type="button"
          onClick={() => { /* could open dialog; for now do nothing */ }}
          className="flex items-center gap-2 px-4 py-2 text-[12px] font-inter text-foreground hover:bg-[#FAFAF7] border-b border-border"
        >
          <Search className="h-3.5 w-3.5 text-virgilio-muted" />
          See all <strong className="font-semibold">{totalAll}</strong> results for
          <span className="font-medium">“{trimmed}”</span>
          <ArrowRight className="h-3 w-3 ml-auto text-virgilio-muted" />
        </button>
      )}

      {/* Ask Gio interpretation chips */}
      {askMode && hasQuery && (
        <div className="px-4 py-3 border-b border-border bg-violet-50/40">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.06em] font-inter font-semibold text-violet-700 mb-2">
            <Sparkles className="h-3 w-3" />
            {askLoading ? 'Gio is thinking\u2026' : askError ? 'Couldn\u2019t parse' : 'Gio understood'}
          </div>
          {askError && (
            <p className="text-[12px] font-inter text-destructive">{askError}</p>
          )}
          {askResult && (
            <div className="flex flex-wrap gap-1.5">
              {[
                ...askResult.seniorityLevels.map(v => ({ k: 'Role', v })),
                ...askResult.cities.map(v => ({ k: 'City', v })),
                ...askResult.countries.map(v => ({ k: 'Country', v })),
                ...askResult.skills.map(v => ({ k: 'Skill', v })),
                ...askResult.stages.map(v => ({ k: 'Stage', v })),
                ...askResult.statuses.map(v => ({ k: 'Status', v })),
                ...(askResult.jobTitle ? [{ k: 'Job', v: askResult.jobTitle }] : []),
                ...(askResult.dates ?? []).map(d => ({ k: 'Date', v: chipLabel(d) })),
              ].slice(0, 8).map((chip, i) => (
                <span key={i} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-white border border-violet-200 text-[11.5px] font-inter text-violet-700">
                  <span className="text-violet-500">{chip.k}</span>
                  <span className="font-medium">{chip.v}</span>
                </span>
              ))}
              {askResult.query && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-white border border-violet-200 text-[11.5px] font-inter text-violet-700">
                  <span className="text-violet-500">Text</span>
                  <span className="font-medium">{askResult.query}</span>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Body */}
      <div ref={listRef} className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {/* Group label when empty state */}
        {!hasQuery && !askMode && recent.items.length > 0 && (
          <GroupLabel
            label="Recent"
            right={<button onClick={recent.clear} className="text-[10px] font-inter font-medium text-virgilio-muted hover:text-foreground">Clear all</button>}
          />
        )}
        {!hasQuery && !askMode && savedViews.length > 0 && recent.items.length > 0 && (
          <div className="h-1" />
        )}
        {!hasQuery && !askMode && savedViews.length > 0 && (
          <GroupLabel label="Jump to" />
        )}

        {/* §16/§17: a failed search is an error with Retry, never "no matches". */}
        {searchError && settled && hasQuery && !askMode && !isLoading && (
          <div className="px-4 py-10">
            <LoadError what="search results" compact onRetry={retrySearch} />
          </div>
        )}

        {/* No matches: the search scene above the recovery rows (Ask Gio, Add as candidate). */}
        {noMatches && (
          <div className="px-4 pt-6 pb-4">
            <AnimatedEmpty
              scene="search"
              size="compact"
              onceKey="global-search"
              title="No matches"
              body={<>Nothing fits {'\u201C'}{trimmed}{'\u201D'}. Try fewer words or check the spelling.</>}
              primary={{ label: 'Clear search', icon: <X size={16} strokeWidth={2} />, onClick: () => onQueryChange('') }}
            />
          </div>
        )}

        {/* Render rows */}
        {rows.length === 0 && !isLoading && !(searchError && hasQuery && !askMode) && (
          <div className="flex h-full flex-col items-center justify-center px-4 py-12 text-center">
            {hasQuery ? (
              <>
                <p className="font-poppins font-semibold text-[14px] text-foreground">No results for “{trimmed}”</p>
                <p className="font-inter text-[12px] text-virgilio-muted mt-1">
                  Check spelling, broaden your scope, or ask Gio in plain English.
                </p>
              </>
            ) : (
              <p className="font-inter text-[12px] text-virgilio-muted">
                Start typing to search candidates, jobs, and saved searches.
              </p>
            )}
          </div>
        )}

        {rows.map((row, i) => (
          <SearchResultRowV2
            key={row.key}
            glyph={row.glyph}
            initials={row.initials}
            icon={row.icon}
            title={row.title}
            sub={row.sub}
            rightMeta={row.rightMeta}
            query={hasQuery && !askMode ? trimmed : ''}
            selected={i === highlighted}
            onClick={row.onSelect}
            onMouseEnter={() => setHighlighted(i)}
          />
        ))}

        {/* Previous results stay while a new search runs; the input shows the spinner. */}
        {isLoading && !askMode && rows.length === 0 && (
          <div className="px-4 py-3 text-[12px] font-inter text-virgilio-muted">Searching…</div>
        )}
      </div>

      {/* Keyboard footer */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-[#FAFAF7] text-[10.5px] font-inter text-virgilio-muted">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <Kbd><ArrowUp className="h-2.5 w-2.5" /></Kbd>
            <Kbd><ArrowDown className="h-2.5 w-2.5" /></Kbd>
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd><CornerDownLeft className="h-2.5 w-2.5" /></Kbd>
            open
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Tab</Kbd>
            scope
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>esc</Kbd>
            close
          </span>
        </div>
        <span className="inline-flex items-center gap-1">
          Powered by <span className="font-semibold text-foreground">Gio</span>
        </span>
      </div>
    </div>
  )
}

function GroupLabel({ label, right }: { label: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-2 pt-2 pb-1">
      <span className="font-inter font-semibold uppercase text-[10px] tracking-[0.06em] text-[#8B8F9E]">
        {label}
      </span>
      {right}
    </div>
  )
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded bg-white border border-border font-mono text-[9.5px] text-virgilio-muted">
      {children}
    </kbd>
  )
}
